// Role-based access + negeri scoping for the Jadual Awalan and Julat Harga modules.
// Mirrors the backend policy in src/lib/scopeNegeriByRole.js — keep the two in sync.
//
//   • Jadual Awalan : BPHPP, BPM (all negeri) + PP, PO (own kodneg). FE/DE/others → no access.
//   • Julat Harga   : BPHPP, BPM (all negeri) + PP, PO, FE, DE (own kodneg). others → no access.
//   • Audit Log     : BPHPP, BPM (all negeri) + PP, PO (own kodneg). others → no access.
//
// The logged-in user ({ role, kodneg, … }) is written to localStorage['user'] at login
// (kodneg = hiesuser.state). It is the reliable source — the Vuex `localStorageuser`
// state is only populated if a page dispatches the `localStorageuser` action, which
// most pages don't, so we read localStorage first and fall back to the store.

// ADMIN is allowed everywhere (all negeri) on the DATA modules, to match the
// backend + full admin menu. SUPERADMIN is a strict SUPERSET of ADMIN: it sees
// every module ADMIN sees, PLUS the four pentadbiran-sistem modules at the
// bottom, which ADMIN is deliberately absent from.
export const MODULE_POLICIES = {
  jadualAwalan: { allowed: ['ADMIN', 'SUPERADMIN', 'BPHPP', 'BPM', 'PP', 'PO'], negeriLocked: ['PP', 'PO'] },
  julatHarga: { allowed: ['ADMIN', 'SUPERADMIN', 'BPHPP', 'BPM', 'PP', 'PO', 'FE', 'DE'], negeriLocked: ['PP', 'PO', 'FE', 'DE'] },
  // Audit Log: mirrors controllers/auditlog/auditlog.js — FULL_ROLES (BPM/BPHPP/
  // ADMIN) see every row; STATE_ROLES (PP/PO) are scoped to NG = kodneg; all
  // other roles get a 403 from the API, so keep them off the route entirely.
  auditLog: { allowed: ['ADMIN', 'SUPERADMIN', 'BPHPP', 'BPM', 'PP', 'PO'], negeriLocked: ['PP', 'PO'] },
  // Data Collapse HES: BPHPP/BPM see all negeri; PP/PO pinned to their own kodneg.
  dataCollapse: { allowed: ['ADMIN', 'SUPERADMIN', 'BPHPP', 'BPM', 'PP', 'PO'], negeriLocked: ['PP', 'PO'] },
  // ── SUPERADMIN-only modules ────────────────────────────────────────────────
  // These four are the whole of SUPERADMIN's menu, and they were taken OFF
  // ADMIN when the role was added. Each mirrors requireSuperAdmin (or
  // permission(['SUPERADMIN'])) on the backend routes. None has a negeri
  // dimension: they are system administration, never per-state.
  //
  // Modul Pengurusan Pengguna — mirrors adminOnly in the backend
  // routes/penggunasistem.route.js.
  pengurusanPengguna: { allowed: ['SUPERADMIN'], negeriLocked: [] },
  // Migrasi Data — it replaces whole tables in the analisis DB.
  migrasiData: { allowed: ['SUPERADMIN'], negeriLocked: [] },
  // Status Sambungan — DB/ClickHouse connection health + the sync trigger.
  dbStatus: { allowed: ['SUPERADMIN'], negeriLocked: [] },
  // Konfigurasi — /admin/config CRUD.
  konfigurasi: { allowed: ['SUPERADMIN'], negeriLocked: [] },
  // Pengurusan Negeri & Daerah — menyunting lookupnegeri + lampiran_daerah, jadual
  // rujukan yang dibaca oleh Jadual Awalan/Lampiran/Wajaran. SUPERADMIN sahaja
  // (mirrors requireSuperAdmin on /negeriDaerah): satu kod negeri/daerah yang
  // salah merosakkan setiap laporan yang bergantung padanya. Tiada dimensi
  // negeri — ia jadual rujukan global, bukan data per-negeri.
  pengurusanNegeriDaerah: { allowed: ['SUPERADMIN'], negeriLocked: [] },
};

function userFrom(store) {
  // Prefer localStorage['user'] (always set at login); fall back to the Vuex store.
  try {
    const u = JSON.parse(window.localStorage.getItem('user'));
    if (u && (u.role || u.kodneg)) return u;
  } catch (e) { /* ignore parse errors, fall through */ }
  const s = (store && store.state && store.state.localStorageuser) || {};
  return Array.isArray(s) ? {} : s;
}

// Can this user access the module at all?
export function canAccess(store, moduleKey) {
  const policy = MODULE_POLICIES[moduleKey];
  if (!policy) return false;
  return policy.allowed.includes(userFrom(store).role);
}

// Negeri lock for a given module. Returns { locked, role, kodneg }.
// locked = the role is pinned to its own negeri (dropdown disabled, negeri = kodneg).
export function negeriLock(store, moduleKey = 'jadualAwalan') {
  const user = userFrom(store);
  const role = user.role;
  const kodneg = user.kodneg ? String(user.kodneg) : null;
  const policy = MODULE_POLICIES[moduleKey] || MODULE_POLICIES.jadualAwalan;
  const locked = policy.negeriLocked.includes(role) && !!kodneg;
  return { locked, role, kodneg };
}

// Kept for readability where callers want the raw list.
export const NEGERI_LOCKED_ROLES = MODULE_POLICIES.jadualAwalan.negeriLocked;
